import { useState } from "react";
import { useCustom, useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";

/**
 * De WhatsApp-tegel bij een relatie.
 *
 * <p>Je kunt niet van tevoren weten of een nummer WhatsApp heeft -- Meta vertelt dat niet, en de
 * oude manier om het te vragen bestaat niet meer. Daarom is deze tegel optimistisch: hij laat het
 * nummer zien en je probeert het gewoon. Weigert Meta omdat er geen WhatsApp op dat nummer zit, dan
 * staat dát er, in de woorden van Meta zelf. Een knop verbergen omdat we het niet zeker weten is
 * slechter: dan kun je niets, en weet je nog steeds niet waarom.
 *
 * <p>Het nummer komt van de server uit het dossier en wordt hier niet ingetypt. Dat scheelt niet
 * alleen een vergissing, het voorkomt ook dat iemand namens het asiel naar een willekeurig nummer
 * appt.
 */
interface Stand {
  beschikbaar: boolean;
  nummer: string | null;
}

interface Antwoord {
  gelukt: boolean;
  berichtId: string | null;
  melding: string | null;
}

export function WhatsAppTile({ relatieId }: { relatieId: string }) {
  const t = useTranslate();
  const [tekst, setTekst] = useState("");
  const [uitkomst, setUitkomst] = useState<Antwoord | null>(null);

  const { data, isLoading } = useCustom<Stand>({
    url: `relaties/${relatieId}/whatsapp`,
    method: "get",
  });
  const { mutate, isLoading: bezig } = useCustomMutation();
  const stand = data?.data;

  if (isLoading) {
    return <p className="fawkes-muted">{t("common.loading")}</p>;
  }
  if (!stand?.beschikbaar) {
    return <p className="fawkes-muted">{t("whatsapp.nietIngesteld")}</p>;
  }
  if (!stand.nummer) {
    return <p className="fawkes-muted">{t("whatsapp.geenNummer")}</p>;
  }

  const stuur = () => {
    setUitkomst(null);
    mutate(
      {
        url: `relaties/${relatieId}/whatsapp`,
        method: "post",
        values: { tekst },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => {
          const uit = res?.data as unknown as Antwoord;
          setUitkomst(uit);
          if (uit?.gelukt) setTekst("");
        },
        onError: () => setUitkomst({ gelukt: false, berichtId: null, melding: t("whatsapp.mislukt") }),
      },
    );
  };

  return (
    <div className="fawkes-wa">
      <p className="fawkes-wa__naar">
        {t("whatsapp.naar")} <b>{stand.nummer}</b>
      </p>
      <textarea
        className="fui-field__input fawkes-wa__tekst"
        rows={3}
        maxLength={4096}
        value={tekst}
        placeholder={t("whatsapp.plaatshouder")}
        aria-label={t("whatsapp.tekstLabel")}
        onChange={(e) => { setTekst(e.target.value); setUitkomst(null); }}
      />
      <div className="fawkes-wa__balk">
        <Button variant="primary" disabled={bezig || !tekst.trim()} onClick={stuur}>
          {t(bezig ? "whatsapp.bezig" : "whatsapp.versturen")}
        </Button>
      </div>
      {uitkomst && (
        <p className={uitkomst.gelukt ? "fawkes-wa__ok" : "fawkes-wa__fout"} role="status">
          {uitkomst.gelukt ? t("whatsapp.verstuurd") : uitkomst.melding || t("whatsapp.mislukt")}
        </p>
      )}
      {/* Zolang binnenkomende berichten nog niet opgehaald worden, is dit een eenrichtingstegel.
          Dat eerlijk zeggen is beter dan een leeg gespreksvenster dat suggereert dat er niets is. */}
      <p className="fawkes-wa__nog">{t("whatsapp.nogGeenGesprek")}</p>
    </div>
  );
}
